import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import {
  CAMPAIGN_CHANNELS,
  invalidateCampaignQueries,
  toCampaignRailRow,
  toCampaignSequenceStep,
  toCampaignSequenceSwitch,
} from '#/entities/campaign'
import type {
  CampaignRailRow,
  CampaignSequenceStep,
  CampaignSequenceSwitch,
} from '#/entities/campaign'
import {
  CampaignStatus,
  StepSendAs,
  StepTaskType,
} from '#/shared/api/generated/communication/models'
import type {
  CampaignReplyHandling,
  CampaignStepRead,
  CampaignUpdate,
} from '#/shared/api/generated/communication/models'
import {
  useAddStepApiV1CampaignsCampaignIdStepsPost,
  useDeleteStepApiV1CampaignsCampaignIdStepsStepIdDelete,
  useCampaignSequenceRulesApiV1CampaignsCampaignIdSequenceGet,
  useGetCampaignApiV1CampaignsCampaignIdGet,
  useUpdateCampaignApiV1CampaignsCampaignIdPatch,
  useUpdateStepApiV1CampaignsCampaignIdStepsStepIdPatch,
} from '#/shared/api/generated/communication/theCargoCommunication'
import { getApiErrorDetail } from '#/shared/lib/api/error-handler'
import { toast } from '#/shared/ui/sonner'

import { CONDITION_TO_API, conditionsFromApi } from './use-campaign-add-step'
import type { CampaignStepCondition } from './use-campaign-add-step'
import { CAMPAIGN_SEND_AS, CAMPAIGN_SEND_RATES } from './use-campaign-composer'
import type {
  CampaignComposerSeed,
  CampaignSendRate,
  CampaignStepDraft,
} from './use-campaign-composer'

/** How long an inline body edit sits before it is written back. */
const BODY_SAVE_DELAY_MS = 1_000

/**
 * Which field on `PATCH /campaigns/{id}` a rail switch flips. A key missing
 * here is one the service does not let anyone change, and renders locked.
 */
const SWITCH_FIELDS: Partial<Record<string, keyof CampaignUpdate>> = {
  reply: 'exit_on_reply',
  book: 'exit_on_booking',
  click: 'exit_on_click',
  businessHours: 'business_hours_only',
  skipWeekends: 'skip_weekends',
}

const EMPTY_SEED: CampaignComposerSeed = {
  body: '',
  subject: '',
  previewText: '',
  sendAs: CAMPAIGN_SEND_AS.thread,
  rate: CAMPAIGN_SEND_RATES.natural,
  fromNumber: '',
  startsAt: null,
  taskType: StepTaskType.call,
  assigneeUserId: '',
  dueOffsetDays: '',
  delayMinutes: null,
  conditions: null,
}

/**
 * The channel-specific half of a step write. A threaded email step replies
 * into step 1's thread and keeps that subject — the service ignores any
 * subject sent with it on purpose, so none is sent: what the composer shows
 * as locked is not on the wire either.
 */
function channelFields(draft: CampaignStepDraft) {
  // A task step is agent work, not a message: its title goes where an email
  // step puts its subject, and it carries no body at all (the service defaults
  // `body` to '' rather than requiring one).
  if (draft.channel === CAMPAIGN_CHANNELS.task) {
    const due = Number(draft.dueOffsetDays)
    return {
      subject: draft.subject,
      task_type: draft.taskType,
      // Null is not "unassigned" — it is the quote's own agent, which is the
      // right default for work about that quote.
      assignee_user_id:
        draft.assigneeUserId === '' ? null : draft.assigneeUserId,
      due_offset_days:
        draft.dueOffsetDays === '' || !Number.isInteger(due) ? null : due,
    }
  }
  if (draft.channel !== CAMPAIGN_CHANNELS.email) {
    return { from_number: draft.fromNumber }
  }
  const threads = draft.sendAs !== CAMPAIGN_SEND_AS.newSubject
  return {
    subject: threads ? undefined : draft.subject,
    preview_text: draft.previewText,
    send_as: threads ? StepSendAs.thread : StepSendAs.new_subject,
  }
}

/** The step's own content, for the composer to open on. */
function toSeed(read: CampaignStepRead | undefined): CampaignComposerSeed {
  if (read == null) return EMPTY_SEED
  return {
    body: read.body,
    subject: read.subject ?? '',
    previewText: read.preview_text ?? '',
    sendAs:
      read.send_as === StepSendAs.new_subject
        ? CAMPAIGN_SEND_AS.newSubject
        : CAMPAIGN_SEND_AS.thread,
    rate: read.send_rate as CampaignSendRate,
    fromNumber: read.from_number ?? '',
    startsAt: read.starts_at ?? null,
    taskType: read.task_type ?? StepTaskType.call,
    assigneeUserId: read.assignee_user_id ?? '',
    dueOffsetDays:
      read.due_offset_days == null ? '' : String(read.due_offset_days),
    delayMinutes: read.delay_minutes,
    conditions: conditionsFromApi(read.only_if),
  }
}

export interface CampaignSequenceRail {
  exitRules: CampaignSequenceSwitch[]
  exitNote: string
  replyHandling: CampaignReplyHandling | null
  coverage: CampaignRailRow[]
  coverageNote: string
  window: CampaignSequenceSwitch[]
}

/**
 * The sequence's steps and its rail.
 *
 * `GET /campaigns/{id}/sequence` describes a step the way the card draws it —
 * timing, state pill, footer line and six figures, all already worded — so
 * nothing here composes a sentence or picks a state. `GET /campaigns/{id}` is
 * still read alongside it for what the card does not show: the threading
 * choice, the send pace and the pinned line the composer opens on.
 *
 * Bodies are edited in place; an edit is written back with
 * `PATCH /steps/{id}` once typing pauses, so the card never needs a save
 * button and a reload shows what was last typed. Rail switches go straight to
 * `PATCH /campaigns/{id}`; a locked switch never reaches it.
 */
export function useCampaignSequence(campaignId: string) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()

  const campaignQuery = useGetCampaignApiV1CampaignsCampaignIdGet(campaignId)
  const rulesQuery =
    useCampaignSequenceRulesApiV1CampaignsCampaignIdSequenceGet(campaignId)
  const reads = campaignQuery.data?.data.steps
  const rules = rulesQuery.data?.data

  const fail = useCallback(
    (error: unknown) =>
      toast.error(
        getApiErrorDetail(error) ?? t('campaigns.detail.actionError'),
      ),
    [t],
  )
  const settle = useCallback(
    () => invalidateCampaignQueries(queryClient),
    [queryClient],
  )

  // Inline edits, keyed by step id, layered over what the service last sent.
  const [drafts, setDrafts] = useState<Partial<Record<string, string>>>({})
  const timers = useRef<Record<string, ReturnType<typeof setTimeout>>>({})
  const updateStep = useUpdateStepApiV1CampaignsCampaignIdStepsStepIdPatch({
    mutation: { onSuccess: settle, onError: fail },
  })
  useEffect(() => {
    const pending = timers.current
    return () => Object.values(pending).forEach(clearTimeout)
  }, [])

  const onBodyChange = useCallback(
    (stepId: string, body: string) => {
      setDrafts((current) => ({ ...current, [stepId]: body }))
      clearTimeout(timers.current[stepId])
      timers.current[stepId] = setTimeout(() => {
        updateStep.mutate({ campaignId, stepId, data: { body } })
      }, BODY_SAVE_DELAY_MS)
    },
    [campaignId, updateStep],
  )

  /**
   * The cards, as `GET /sequence` words them. The only thing layered on top is
   * an unsaved body: a card being typed into must show what was typed, not the
   * text the service last confirmed, or the caret jumps back on every refetch.
   */
  const steps: CampaignSequenceStep[] = useMemo(
    () =>
      (rules?.steps ?? []).map((step) => {
        const card = toCampaignSequenceStep(step)
        const draft = drafts[card.id]
        return draft == null ? card : { ...card, body: draft }
      }),
    [rules, drafts],
  )

  const updateCampaign = useUpdateCampaignApiV1CampaignsCampaignIdPatch({
    mutation: { onSuccess: settle, onError: fail },
  })
  const onToggleSwitch = useCallback(
    (key: string, enabled: boolean) => {
      const field = SWITCH_FIELDS[key]
      if (field == null) return
      updateCampaign.mutate({ campaignId, data: { [field]: enabled } })
    },
    [campaignId, updateCampaign],
  )

  const rail: CampaignSequenceRail | null = useMemo(() => {
    if (rules == null) return null
    const withLock = (row: CampaignSequenceSwitch): CampaignSequenceSwitch => ({
      ...row,
      locked: row.locked === true || SWITCH_FIELDS[row.key] == null,
    })
    return {
      exitRules: rules.exit_rules.map(toCampaignSequenceSwitch).map(withLock),
      exitNote: rules.exit_note,
      replyHandling: rules.reply_handling,
      coverage: rules.coverage.map(toCampaignRailRow),
      coverageNote: rules.coverage_note,
      window: rules.window.map(toCampaignSequenceSwitch).map(withLock),
    }
  }, [rules])

  // Which step is open in its own composer. Steps are append-only, so editing
  // is a PATCH of the one card rather than anything that moves the sequence.
  const [editingStepId, setEditingStepId] = useState<string | null>(null)
  // The composer needs what the card does not show — threading, pace, the line
  // it sends from — which only `GET /campaigns/{id}` carries. Keyed by step id,
  // the same id `/sequence` reports.
  const seeds = useMemo(
    () => new Map((reads ?? []).map((read) => [read.id, toSeed(read)])),
    [reads],
  )

  const onSaveStep = useCallback(
    (stepId: string, draft: CampaignStepDraft) => {
      updateStep.mutate(
        {
          campaignId,
          stepId,
          data: {
            body: draft.body,
            send_rate: draft.rate,
            ...channelFields(draft),
            starts_at: draft.startsAt,
            // Null only if the composer never had a stored wait to show —
            // then there is nothing to change, so the field is left off.
            delay_minutes: draft.delayMinutes ?? undefined,
            only_if: draft.conditions?.map((c) => CONDITION_TO_API[c]),
          },
        },
        {
          onSuccess: () => {
            toast.success(t('campaigns.sequence.toasts.stepSaved'))
            setEditingStepId(null)
          },
        },
      )
    },
    [campaignId, updateStep, t],
  )

  /**
   * Move a step to another position. The service renumbers everything between
   * and clamps an out-of-range index rather than erroring, so the caller only
   * has to say where the step should land.
   */
  const onMoveStep = useCallback(
    (stepId: string, stepIndex: number) => {
      updateStep.mutate(
        { campaignId, stepId, data: { step_index: stepIndex } },
        {
          onSuccess: () =>
            toast.success(t('campaigns.sequence.toasts.stepMoved')),
        },
      )
    },
    [campaignId, updateStep, t],
  )

  /** Retiring a step keeps its send history attached; deleting would not. */
  const onToggleActive = useCallback(
    (stepId: string, isActive: boolean) => {
      updateStep.mutate(
        { campaignId, stepId, data: { is_active: isActive } },
        {
          onSuccess: () =>
            toast.success(
              t(
                isActive
                  ? 'campaigns.sequence.toasts.stepActivated'
                  : 'campaigns.sequence.toasts.stepDeactivated',
              ),
            ),
        },
      )
    },
    [campaignId, updateStep, t],
  )

  const deleteStep = useDeleteStepApiV1CampaignsCampaignIdStepsStepIdDelete({
    mutation: {
      onSuccess: () => {
        toast.success(t('campaigns.sequence.toasts.stepDeleted'))
        settle()
      },
      onError: fail,
    },
  })

  const addStep = useAddStepApiV1CampaignsCampaignIdStepsPost({
    mutation: {
      onSuccess: () => {
        toast.success(t('campaigns.sequence.toasts.stepAdded'))
        settle()
      },
      onError: fail,
    },
  })

  /** Returns false when the picker's choice cannot be sent, so the composer stays open. */
  const onAddStep = useCallback(
    (
      draft: CampaignStepDraft,
      timing: {
        delayMinutes: number | null
        conditions: CampaignStepCondition[]
      },
    ): boolean => {
      const { delayMinutes } = timing
      if (delayMinutes == null) {
        toast.error(t('campaigns.sequence.addStep.customDaysMissing'))
        return false
      }
      addStep.mutate({
        campaignId,
        data: {
          channel: draft.channel,
          delay_minutes: delayMinutes,
          only_if: timing.conditions.map((c) => CONDITION_TO_API[c]),
          body: draft.body,
          send_rate: draft.rate,
          ...channelFields(draft),
          starts_at: draft.startsAt,
        },
      })
      return true
    },
    [addStep, campaignId, t],
  )

  return {
    steps,
    isLoading: rulesQuery.isPending,
    onBodyChange,
    rail,
    onToggleSwitch,
    isSavingSwitch: updateCampaign.isPending,
    onAddStep,
    isAddingStep: addStep.isPending,
    editingStepId,
    onEditStep: setEditingStepId,
    onCancelEdit: () => setEditingStepId(null),
    seedFor: (stepId: string) => seeds.get(stepId),
    onSaveStep,
    isSavingStep: updateStep.isPending,
    onToggleActive,
    onMoveStep,
    onDeleteStep: (stepId: string) => deleteStep.mutate({ campaignId, stepId }),
    /** Steps are removable only while the campaign is a draft. */
    canDeleteSteps: campaignQuery.data?.data.status === CampaignStatus.draft,
  }
}

export type UseCampaignSequenceResult = ReturnType<typeof useCampaignSequence>
